import type { App } from 'vue'
import { globalEventBus, TYPE, useToast } from 'vue-toastification'
import { getUserFriendlyErrorMessage } from '@/utils/error-message'

type ToastEvent = {
  type?: string
  content?: unknown
}

let toastEventsAreLocalized = false
let browserErrorsAreHandled = false

function isErrorContent(value: unknown) {
  return typeof value === 'string' || value instanceof Error || Boolean(value && typeof value === 'object')
}

function installToastLocalization() {
  if (toastEventsAreLocalized) return

  const originalEmit = globalEventBus.emit.bind(globalEventBus)

  globalEventBus.emit = ((eventType: string, event: unknown) => {
    const toastEvent = event as ToastEvent | undefined

    if (eventType === 'add' && toastEvent?.type === TYPE.ERROR && isErrorContent(toastEvent.content)) {
      toastEvent.content = getUserFriendlyErrorMessage(toastEvent.content)
    }

    return originalEmit(eventType as never, event as never)
  }) as typeof globalEventBus.emit

  toastEventsAreLocalized = true
}

function showUnhandledError(error: unknown) {
  if (import.meta.env.SSR || isCancelledOperation(error)) return

  useToast().error(getUserFriendlyErrorMessage(error))
}

function isCancelledOperation(error: unknown) {
  if (!error || typeof error !== 'object') return false

  const candidate = error as { code?: unknown; name?: unknown; message?: unknown }
  const message = typeof candidate.message === 'string' ? candidate.message.toLowerCase() : ''

  return (
    candidate.code === 'ERR_CANCELED' ||
    candidate.name === 'AbortError' ||
    candidate.name === 'CanceledError' ||
    candidate.name === 'CancelledError' ||
    ['cancelled', 'canceled', 'cancellederror', 'cancelederror'].includes(message)
  )
}

function installBrowserErrorHandlers() {
  if (import.meta.env.SSR || browserErrorsAreHandled) return

  window.addEventListener('unhandledrejection', (event) => {
    showUnhandledError(event.reason)
  })

  browserErrorsAreHandled = true
}

/** Installs Arabic, user-safe messaging for errors surfaced anywhere in the client app. */
export function installUserFriendlyErrorHandling(app: App) {
  installToastLocalization()

  const previousErrorHandler = app.config.errorHandler
  app.config.errorHandler = (error, instance, info) => {
    showUnhandledError(error)

    if (previousErrorHandler) {
      previousErrorHandler(error, instance, info)
      return
    }

    console.error(error)
  }

  installBrowserErrorHandlers()
}
